"use client";

import { cn } from "@gigpro/ui/tailwind/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { Dialog as SheetPrimitive } from "radix-ui";
import type * as React from "react";
import { Close } from "../../Icons";
import { usePortalScope } from "../PortalScope";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = ({ ref, className, ...props }: React.ComponentProps<typeof SheetPrimitive.Overlay>) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/60 data-[state=closed]:pointer-events-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
);
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "fixed z-50 max-h-full bg-white shadow-back transition ease-in-out data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:animate-in data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom: "inset-x-0 bottom-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: { side: "right" },
  },
);

interface SheetContentProps
  extends React.ComponentProps<typeof SheetPrimitive.Content>, VariantProps<typeof sheetVariants> {}

const SheetContent = ({ ref, side = "right", className, children, ...props }: SheetContentProps) => {
  const scope = usePortalScope();
  return (
    <SheetPortal forceMount={props.forceMount}>
      <SheetOverlay forceMount={props.forceMount} />
      <SheetPrimitive.Content ref={ref} className={cn(sheetVariants({ side }), className)} {...scope} {...props}>
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
};

SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetClose = ({ ref, className, ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) => (
  <SheetPrimitive.Close
    ref={ref}
    className={cn("size-4 text-brand focus:outline-none disabled:pointer-events-none", className)}
    {...props}
  >
    <Close className="size-full" />
    <span className="sr-only">Close</span>
  </SheetPrimitive.Close>
);
SheetClose.displayName = "SheetClose";

interface SheetHeaderProps extends React.ComponentProps<"div"> {
  hideCloseButton?: boolean;
}
const SheetHeader = ({ className, children, hideCloseButton, ...props }: SheetHeaderProps) => (
  <div
    className={cn("sticky inset-x-0 z-10 flex flex-col gap-1.5 rounded-sm bg-white p-4 pr-3 text-left", className)}
    {...props}
  >
    {children}
    {!hideCloseButton && <SheetClose className="absolute top-4 right-4" />}
  </div>
);
SheetHeader.displayName = "SheetHeader";

const SheetTitle = ({
  ref,
  className,
  description,
  hideCloseButton,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title> & {
  description?: React.ReactNode;
  hideCloseButton?: boolean;
}) => (
  <SheetHeader className={className} hideCloseButton={hideCloseButton}>
    <SheetPrimitive.Title ref={ref} className="text-large leading-none font-semibold tracking-tight" {...props} />
    <SheetPrimitive.Description className={cn("mt-0.5 text-normal tracking-tight text-gray", !description && "hidden")}>
      {description}
    </SheetPrimitive.Description>
  </SheetHeader>
);
SheetTitle.displayName = SheetPrimitive.Title.displayName;

export { Sheet, SheetClose, SheetContent, SheetTrigger, SheetHeader, SheetTitle };
